import { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload, Eye, EyeOff, Trash2, ArrowUp, ArrowDown, Loader2, Image as ImageIcon } from 'lucide-react'
import {
  adminGetCarousel, updateCarouselConfig, uploadCarouselPhoto,
  updateCarouselPhoto, reorderCarousel, deleteCarouselPhoto,
  type Effet, type CarouselPhoto,
} from '../../api/carousel'

const MARQUES = [
  { key: 'husqvarna', label: 'Husqvarna' },
  { key: 'honda', label: 'Honda' },
]

const EFFETS: { key: Effet; label: string; desc: string }[] = [
  { key: 'morph', label: 'Morph', desc: 'Fondu + léger flou et zoom (effet actuel).' },
  { key: 'fade', label: 'Fondu', desc: 'Fondu enchaîné simple.' },
  { key: 'slide', label: 'Glissement', desc: 'Les photos glissent horizontalement.' },
]

export function AdminCarousels() {
  const [marque, setMarque] = useState('husqvarna')
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {MARQUES.map((m) => (
          <button
            key={m.key}
            onClick={() => setMarque(m.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              marque === m.key ? 'bg-orange text-white' : 'bg-sand text-ink/70 ring-1 ring-navy/15 hover:bg-sand/60'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <CarouselEditor key={marque} marque={marque} />
    </div>
  )
}

function CarouselEditor({ marque }: { marque: string }) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [busyUpload, setBusyUpload] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['carousel-admin', marque],
    queryFn: () => adminGetCarousel(marque),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['carousel-admin', marque] })
    qc.invalidateQueries({ queryKey: ['carousel', marque] })
  }

  const configMut = useMutation({
    mutationFn: (cfg: { effet?: Effet; vitesse_ms?: number }) => updateCarouselConfig(marque, cfg),
    onSuccess: invalidate,
  })
  const toggleMut = useMutation({
    mutationFn: (p: CarouselPhoto) => updateCarouselPhoto(p.id, { visible: !p.visible }),
    onSuccess: invalidate,
  })
  const delMut = useMutation({
    mutationFn: (id: number) => deleteCarouselPhoto(id),
    onSuccess: invalidate,
  })
  const reorderMut = useMutation({
    mutationFn: (ids: number[]) => reorderCarousel(marque, ids),
    onSuccess: invalidate,
  })

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setErreur(null)
    setBusyUpload(true)
    try {
      await uploadCarouselPhoto(marque, file)
      invalidate()
    } catch (err: any) {
      setErreur(err?.response?.data?.detail || "Échec de l'envoi.")
    } finally {
      setBusyUpload(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  function move(i: number, dir: -1 | 1) {
    if (!data) return
    const ids = data.photos.map((p) => p.id)
    const j = i + dir
    if (j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    reorderMut.mutate(ids)
  }

  if (isLoading || !data) {
    return <div className="grid place-items-center py-20 text-ink/50"><Loader2 className="h-6 w-6 animate-spin" /></div>
  }

  const visibles = data.photos.filter((p) => p.visible).length

  return (
    <div className="mt-5">
      {/* Réglages d'affichage */}
      <div className="rounded-xl border border-navy/10 bg-sand/50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink/55">Type de défilement</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {EFFETS.map((ef) => (
            <button
              key={ef.key}
              title={ef.desc}
              onClick={() => configMut.mutate({ effet: ef.key })}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                data.config.effet === ef.key ? 'bg-navy text-white' : 'bg-white text-ink/70 ring-1 ring-navy/15 hover:bg-white/60'
              }`}
            >
              {ef.label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-ink/50">{EFFETS.find((e) => e.key === data.config.effet)?.desc}</p>

        <div className="mt-5">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink/55">
            Vitesse — une photo toutes les {(data.config.vitesse_ms / 1000).toFixed(1)} s
          </label>
          <input
            type="range"
            min={1000}
            max={8000}
            step={500}
            defaultValue={data.config.vitesse_ms}
            onChange={(e) => configMut.mutate({ vitesse_ms: Number(e.target.value) })}
            className="mt-2 w-full max-w-md accent-orange"
          />
          <div className="flex max-w-md justify-between text-[11px] text-ink/40">
            <span>Rapide (1 s)</span><span>Lent (8 s)</span>
          </div>
        </div>
      </div>

      {/* En-tête photos + upload */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-ink/55">{data.photos.length} photo(s) · {visibles} affichée(s)</p>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busyUpload}
          className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-dk disabled:opacity-50"
        >
          {busyUpload ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Ajouter une photo
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="hidden" />
      </div>
      {erreur && <p className="mt-2 text-sm text-red-600">{erreur}</p>}

      {/* Grille de photos */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {data.photos.map((p, i) => (
          <div key={p.id} className={`overflow-hidden rounded-xl border border-navy/10 ${p.visible ? 'bg-white' : 'bg-sand/60 opacity-60'}`}>
            <div className="grid h-32 place-items-center bg-night p-2">
              <img src={p.src} alt="" className="max-h-28 w-auto object-contain" />
            </div>
            <div className="flex items-center justify-between gap-1 p-2">
              <div className="flex gap-0.5">
                <button onClick={() => move(i, -1)} disabled={i === 0} title="Monter" className="rounded-md p-1.5 text-ink/50 hover:bg-sand disabled:opacity-30">
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === data.photos.length - 1} title="Descendre" className="rounded-md p-1.5 text-ink/50 hover:bg-sand disabled:opacity-30">
                  <ArrowDown className="h-4 w-4" />
                </button>
              </div>
              <div className="flex gap-0.5">
                <button onClick={() => toggleMut.mutate(p)} title={p.visible ? 'Masquer' : 'Afficher'} className="rounded-md p-1.5 text-ink/50 hover:bg-sand hover:text-navy">
                  {p.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
                <button onClick={() => { if (confirm('Supprimer cette photo ?')) delMut.mutate(p.id) }} title="Supprimer" className="rounded-md p-1.5 text-ink/50 hover:bg-red-50 hover:text-red-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {data.photos.length === 0 && (
          <div className="col-span-full grid place-items-center gap-2 py-12 text-ink/40">
            <ImageIcon className="h-8 w-8" />
            <p className="text-sm">Aucune photo. Ajoutez-en une.</p>
          </div>
        )}
      </div>
    </div>
  )
}
